import React, { useLayoutEffect, useMemo } from 'react';
import { useToggle } from 'react-use';

import classNames from 'classnames';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import classes from './SidebarCollapseMenu.module.scss';
import { SidebarItem } from '../SidebarItem/SidebarItem';
import { SidebarNavLink } from '../SidebarNavLink/SidebarNavLink';
import { CollapseMenuItem } from '../../../../interfaces/system/SidebarNavConfig';
import { getNavigationIcon } from '../../../../utils/navigationUtils/getNavigationIcon/getNavigationIcon';
import { useIsActivePage } from '../../../../hooks/useIsActivePage';
import { getIsActivePageByUrl } from '../../../../utils/angularUtils';
import { useSidebarContext } from '../../hooks/SidebarContext';

type SidebarCollapseMenuProps = {
  item: CollapseMenuItem;
};

/**
 * Collapsible sidebar group that expands/collapses its child links.
 * Auto-opens if a child page is active on mount (sync, no flicker)
 * and on subsequent navigations (via useLayoutEffect).
 * User can manually collapse even when a child is active — parent stays highlighted.
 */
const SidebarCollapseMenu = ({ item }: SidebarCollapseMenuProps) => {
  const { icon, label, items: subItems } = item;
  const { isCollapsed } = useSidebarContext();

  const pathList = useMemo(() => subItems.map((subItem) => subItem.path), [subItems]);
  // Sync initial state to avoid arrow transition flicker on page load
  const [isOpen, toggleOpen] = useToggle(pathList.some(getIsActivePageByUrl));

  const isActive = useIsActivePage({ urls: pathList });

  // Auto-open on navigation to a child page (useLayoutEffect to prevent flicker)
  useLayoutEffect(() => {
    if (isActive && !isOpen) {
      toggleOpen(true);
    }
  }, [isActive]);

  return (
    <div>
      <SidebarItem
        as="button"
        onClick={toggleOpen}
        startIcon={getNavigationIcon(icon)}
        label={label}
        endIcon={
          <KeyboardArrowDownRoundedIcon
            className={classNames(classes.arrow, { [classes.open]: isOpen })}
          />
        }
        active={isOpen || !!isActive}
        collapsed={isCollapsed}
      />
      {isOpen && (
        <div className={classes.children}>
          {subItems.map((subItem) => (
            <SidebarNavLink item={subItem} key={subItem.path} />
          ))}
        </div>
      )}
    </div>
  );
};

export { SidebarCollapseMenu };
